import React, { useEffect, useState } from 'react';
import { EMPTY_RECORD, useDetailRecord } from 'helpers/detailRequest';
import { confirmAlert, TwIconButton, DetailRecordFallback } from 'components/common';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaUserTag, FaEdit, FaTrash, FaEllipsisV,
  FaBolt, FaHistory, FaStream,
} from 'react-icons/fa';
import {
  MdPerson, MdEmail, MdPhone, MdBusiness, MdWork,
} from 'react-icons/md';
import { getContact } from 'redux/crm/contactSlice';
import apiAxios from 'helpers/apiAxios';
import { escapeHtml } from 'helpers/utils';
import CrmDetailLayout from 'components/app/crm/shared/CrmDetailLayout';
import ContactDetailInfo    from './ContactDetailInfo';
import ContactActivity      from './ContactActivity';
import AssignContactModal   from '../AssignContactModal';
import EditContactDrawer    from '../EditContactDrawer';
import RecipientActivityPanel from 'components/app/crm/shared/RecipientActivityPanel';
import useBreadcrumb from 'hooks/useBreadcrumb';
import PropTypes from 'prop-types';

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'detail',   label: 'Detail',          Icon: FaStream, accent: '#3b82f6' },
  { key: 'actNotes', label: 'Activity & Notes', Icon: FaBolt,   accent: '#ec4899' },
  { key: 'history',  label: 'History',          Icon: FaHistory, accent: '#64748b' },
];

// ── Actions menu ──────────────────────────────────────────────────────────────
const ActionsMenu = ({ allow_assign, allow_edit, allow_delete, onAssign, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      {allow_assign && (
        <button type="button" onClick={onAssign}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-semibold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-border-0 tw-cursor-pointer tw-transition-colors">
          <FaUserTag size={11} /> Assign to
        </button>
      )}
      {(allow_edit || allow_delete) && (
        <div className="tw-relative">
          <TwIconButton icon={FaEllipsisV} iconSize={12} variant="secondary" size="lg" onClick={() => setOpen(v => !v)} title="More actions" />
          {open && (
            <>
              <div className="tw-fixed tw-inset-0 tw-z-40" onClick={() => setOpen(false)} />
              <div className="tw-absolute tw-right-0 tw-top-10 tw-z-50 tw-min-w-[140px] tw-bg-white tw-rounded-lg tw-shadow-xl tw-border tw-border-slate-200 tw-py-1.5">
                {allow_edit && (
                  <button type="button" onClick={() => { setOpen(false); onEdit(); }}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-blue-600 hover:tw-bg-blue-50 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-left">
                    <FaEdit size={12} /> Edit
                  </button>
                )}
                {allow_edit && allow_delete && <div className="tw-border-t tw-border-slate-100 tw-mx-2 tw-my-1" />}
                {allow_delete && (
                  <button type="button" onClick={() => { setOpen(false); onDelete(); }}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-left">
                    <FaTrash size={12} /> Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

ActionsMenu.propTypes = {
  allow_assign: PropTypes.bool,
  allow_edit: PropTypes.bool,
  allow_delete: PropTypes.bool,
  onAssign: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

// ── ContactDetail ─────────────────────────────────────────────────────────────
const ContactDetail = () => {
  const params   = useParams();
  // Only the record in the URL — the persisted store can still hold the one
  // viewed last (e.g. when opened from an email link).
  const { record, waiting, error } = useDetailRecord('crm_contact', params.id);
  const contact = record || EMPTY_RECORD;

  // Breadcrumb: hiển thị tên contact thay vì id
  useBreadcrumb(
    contact
      ? [
          { label: 'Contacts', to: '/crm/contacts/list' },
          { label: contact.name || `Contact ${params.id}` }
        ]
      : null
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab,       setActiveTab]       = useState('detail');
  const [mountedTabs,     setMountedTabs]     = useState(new Set(['detail']));
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditDrawer,  setShowEditDrawer]  = useState(false);

  useEffect(() => { dispatch(getContact(params.id)); }, [params.id, dispatch]);

  const switchTab = (key) => {
    setActiveTab(key);
    setMountedTabs(prev => new Set([...prev, key]));
  };

  const { allow_edit, allow_delete, allow_assign } = contact?.permissions || {};

  const valid = (v) => v != null && v !== '' && v !== 'undefined' && v !== 'null';

  const handleDelete = () =>
    confirmAlert({
      title: 'Delete contact',
      message: `Delete contact "<strong>${escapeHtml(contact?.name)}</strong>"? This cannot be undone.`,
      type: 'danger',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Delete', variant: 'danger',
          onClick: async () => {
            try {
              await apiAxios.delete(`/crm/contacts/${contact.id}`);
              toast.success('Contact deleted.');
              navigate('/crm/contacts/list');
            } catch (e) {
              toast.error(e.response?.data?.message || e.message || 'Failed to delete contact.');
            }
          },
        },
        { label: 'Cancel', onClick: () => {} },
      ],
    });

  if (!record) return <DetailRecordFallback waiting={waiting} title="Contact not available" error={error} onRetry={() => dispatch(getContact(params.id))} />;

  return (
    <>
      <CrmDetailLayout
        avatar={
          <span className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-blue-100 tw-text-blue-600 tw-text-lg tw-font-bold">
            {(contact?.first_name?.[0] || contact?.name?.[0] || '?').toUpperCase()}
          </span>
        }
        title={contact?.name}
        badges={[
          contact?.id && (
            <span key="id" className="tw-text-[11px] tw-font-mono tw-text-slate-400 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-md">
              ID#{contact.id}
            </span>
          ),
        ].filter(Boolean)}
        chips={[
          valid(contact?.position) && { icon: MdWork,     label: contact.position },
          valid(contact?.company?.name) && { icon: MdBusiness, label: contact.company.name },
          valid(contact?.email)    && { icon: MdEmail,    label: contact.email,  href: `mailto:${contact.email}` },
          valid(contact?.mobile)   && { icon: MdPhone,    label: contact.mobile, href: `tel:${contact.mobile}` },
          valid(contact?.country)  && { icon: MdPerson,   label: contact.country },
        ].filter(Boolean)}
        actions={
          <ActionsMenu
            allow_assign={allow_assign}
            allow_edit={allow_edit}
            allow_delete={allow_delete}
            onAssign={() => setShowAssignModal(true)}
            onEdit={() => setShowEditDrawer(true)}
            onDelete={handleDelete}
          />
        }
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={switchTab}
      >
        {/* Detail */}
        {mountedTabs.has('detail') && (
          <div hidden={activeTab !== 'detail'}>
            <ContactDetailInfo contact={contact} />
          </div>
        )}

        {/* Activity & Notes */}
        {mountedTabs.has('actNotes') && (
          <div hidden={activeTab !== 'actNotes'}>
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-p-5">
              {contact?.id && (
                <RecipientActivityPanel recipientType="contact" recipientId={contact.id} />
              )}
            </div>
          </div>
        )}

        {/* History */}
        {mountedTabs.has('history') && (
          <div hidden={activeTab !== 'history'}>
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-p-5">
              <ContactActivity />
            </div>
          </div>
        )}
      </CrmDetailLayout>

      <AssignContactModal
        showModal={showAssignModal}
        setshowModal={setShowAssignModal}
        contactId={contact?.id}
      />

      <EditContactDrawer
        show={showEditDrawer}
        onHide={() => setShowEditDrawer(false)}
        customer={contact?.company}
        contact={contact}
        onSaved={() => dispatch(getContact(params.id))}
      />
    </>
  );
};

export default ContactDetail;
